import React from "react";
import { View, StyleSheet, Pressable } from "react-native";
import CustomText from "../../common/general/CustomText/CustomText";
import { Assessment } from "../../svgs/CustomCoursework";
import CustomButton from "../../common/general/CustomButton/CustomButton";
import CheckedBadge from "../../svgs/common/CheckedBadge";

/* eslint-disable @typescript-eslint/prefer-ts-expect-error */

interface AssessmentCardProps {
  onButtonPress: () => void;
  grade: number;
  status?: "RUNNING" | "COMPLETED";
  isEvaluationPending: boolean;
  onPress: () => void;
  onHoverIn: () => void;
  onHoverOut: () => void;
  isHovered: boolean;
}

interface AssessmentCardContentProps {
  onButtonPress: () => void;
  status: "RUNNING" | "COMPLETED";
  grade: number;
  isEvaluationPending: boolean;
}

function AssessmentCardContent({
  onButtonPress = () => {},
  status = null,
  grade,
  isEvaluationPending = false,
}: AssessmentCardContentProps) {
  return (
    <>
      <View style={styles.resourceItemLeft}>
        <View style={styles.textContainer}>
          <Assessment fill="#101828" style={{ marginTop: "2px" }} />
          <CustomText text="Lesson Assessment" weight="bold" style={styles.resourceItemTitle} />
        </View>
      </View>
      {status === "COMPLETED" ? (
        <View style={styles.buttonsContainer}>
          <View style={styles.gradeContainer}>
            {!!grade && (
              <View style={styles.gradeTag}>
                <CustomText
                  text={`${grade}/100`}
                  useTranslationText={false}
                  size="s"
                  weight="ultraBold"
                  style={{ color: "#fff" }}
                />
              </View>
            )}
            {isEvaluationPending && !grade && (
              <View style={styles.gradeTagUngraded}>
                <CustomText
                  text="Evaluation Pending"
                  useTranslationText={false}
                  size="s"
                  weight="ultraBold"
                  style={{ color: "#fff" }}
                />
              </View>
            )}
            {!isEvaluationPending && (
              <CustomButton
                styleType="primary"
                text="Retake"
                textStyle={styles.buttonText}
                onPress={onButtonPress}
                style={{ backgroundColor: "#3E68FE" }}
              />
            )}
            <CheckedBadge />
          </View>
        </View>
      ) : isEvaluationPending ? (
        <View style={styles.gradeTagUngraded}>
          <CustomText
            text="Evaluation Pending"
            useTranslationText={false}
            size="s"
            weight="ultraBold"
            style={{ color: "#fff" }}
          />
        </View>
      ) : (
        <View style={styles.buttonsContainer}>
          <CustomButton
            styleType="primary"
            text={`${status === "RUNNING" ? "Resume" : "Begin"} Assessment`}
            textStyle={styles.buttonText}
            onPress={onButtonPress}
            style={{ backgroundColor: "#3E68FE" }}
          />
        </View>
      )}
    </>
  );
}

function AssessmentCard({
  onButtonPress = () => {},
  status = null,
  grade,
  isEvaluationPending = false,
  onPress = () => {},
  onHoverIn = () => {},
  onHoverOut = () => {},
  isHovered = false,
}: AssessmentCardProps) {
  if (status !== "COMPLETED" || (status === "COMPLETED" && isEvaluationPending)) {
    return (
      <View style={styles.cardContainer}>
        <AssessmentCardContent
          onButtonPress={onButtonPress}
          status={status}
          grade={grade}
          isEvaluationPending={isEvaluationPending}
        />
      </View>
    );
  }

  return (
    <Pressable
      onPress={onPress}
      onHoverIn={onHoverIn}
      onHoverOut={onHoverOut}
      style={({ pressed }) => [
        styles.cardContainer,
        isHovered && styles.cardContainerHovered,
        pressed && styles.cardContainerPressed,
      ]}>
      <AssessmentCardContent
        onButtonPress={onButtonPress}
        status={status}
        grade={grade}
        isEvaluationPending={isEvaluationPending}
      />
    </Pressable>
  );
}

const styles = StyleSheet.create({
  cardContainer: {
    marginVertical: 8,
    marginHorizontal: 1,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingVertical: 16,
    paddingHorizontal: 24,
    minHeight: 75,
    borderWidth: 1,
    borderColor: "#EAECF0",
    borderRadius: 16,
    // @ts-expect-error - Web CSS property
    outlineWidth: 0,
    outlineColor: "transparent",
    outlineStyle: "solid",
    transition: "all 200ms cubic-bezier(0.4, 0, 0.2, 1)",
  },
  resourceItemLeft: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  textContainer: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    gap: 8,
  },
  resourceItemTitle: {
    marginBottom: 4,
  },
  buttonText: {
    fontSize: 14,
    fontWeight: "700",
  },
  gradeContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 20,
  },
  gradeTag: {
    padding: 8,
    backgroundColor: "#0084FF",
    borderRadius: 100,
  },
  gradeTagUngraded: {
    padding: 8,
    backgroundColor: "#D0D5DD",
    borderRadius: 100,
  },
  buttonsContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 12,
  },
  cardContainerHovered: {
    // @ts-expect-error - Web CSS property
    outlineWidth: 2,
    outlineColor: "#D0D5DD",
    outlineStyle: "solid",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
  },
  cardContainerPressed: {
    backgroundColor: "#F5F5F5",
  },
});

export default AssessmentCard;
